import { useRef, useCallback, useEffect } from "react";
import API_BASE_URL from "../config";
import { useJobsContext } from "../contexts/JobsProvider";
import { isTerminalStatus } from "../utils/jobStatus";

// Re-checks any still-pending job directly, independent of the shared snapshot.
// Safety net for a job that scrolls off the snapshot's global jobs_list_limit if
// enough other jobs are created elsewhere while it's still running.
const SAFETY_NET_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_JOB_WAITER_SAFETYNET_MS) ||
      process.env.REACT_APP_JOB_WAITER_SAFETYNET_MS,
  ) || 15000;

/**
 * Waits for a specific background job to reach a terminal state, using the
 * shared JobsProvider SSE/poll feed rather than each caller opening its own
 * poll loop. Replaces the duplicated pollJob/tick timers in LVWorkflow,
 * Workflow, CompliancePipeline and DiscoveryView.
 *
 * waitFor(jobId) always resolves with the full job row (fetched once terminal,
 * since the shared snapshot omits `result`/`error` — see job_store.list_jobs vs
 * get_job) and only rejects on a genuine fetch/network error; callers keep
 * deciding done vs failed themselves, exactly as before.
 */
export default function useJobWaiter() {
  const { jobs } = useJobsContext();
  const pendingRef = useRef(new Map()); // jobId -> { resolve, reject, settling }

  const fetchJob = useCallback(async (jobId) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/jobs/${jobId}`);
    if (!res.ok) throw new Error(`Job-Status ${res.status}`);
    return res.json();
  }, []);

  const settle = useCallback(async (jobId) => {
    const entry = pendingRef.current.get(jobId);
    if (!entry || entry.settling) return;
    entry.settling = true;
    try {
      const job = await fetchJob(jobId);
      if (!pendingRef.current.has(jobId)) return; // settled elsewhere meanwhile
      if (isTerminalStatus(job.status)) {
        pendingRef.current.delete(jobId);
        entry.resolve(job);
        return;
      }
    } catch (e) {
      if (pendingRef.current.has(jobId)) {
        pendingRef.current.delete(jobId);
        entry.reject(e);
        return;
      }
    }
    entry.settling = false;
  }, [fetchJob]);

  // React to shared snapshot changes (SSE or poll-fallback, whichever is live).
  useEffect(() => {
    if (pendingRef.current.size === 0) return;
    for (const job of jobs) {
      if (pendingRef.current.has(job.id) && isTerminalStatus(job.status)) {
        settle(job.id);
      }
    }
  }, [jobs, settle]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      for (const jobId of pendingRef.current.keys()) settle(jobId);
    }, SAFETY_NET_MS);
    return () => clearInterval(intervalId);
  }, [settle]);

  const waitFor = useCallback((jobId) => {
    return new Promise((resolve, reject) => {
      pendingRef.current.set(jobId, { resolve, reject, settling: false });
      // Immediate check — matches the old pollJob's immediate-first-tick behavior,
      // and covers a job that's already terminal by the time waitFor is called.
      settle(jobId);
    });
  }, [settle]);

  return { waitFor };
}
